import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { type LegalLink } from '@/services/legalService';
import { useContent } from '@/hooks/useContent';
import { LegalDocumentModal } from '@/components/common/LegalDocumentModal';

interface Props {
  links: (LegalLink | null)[];
  /** 'caption' for the pre-login footer; 'body2' for the app-shell footer */
  variant?: 'caption' | 'body2';
  spacing?: number;
}


const FONT_SIZE: Record<'caption' | 'body2', string> = {
  caption: '0.6875rem',
  body2:   '0.75rem',
};

/**
 * Renders a row of legal document links.
 *
 * Visibility rules:
 *   - null entries (no document configured for the facility) → hidden
 *   - non-null entries (document row exists and is active) → always shown
 *
 * Link behaviour:
 *   - url present  → opens in a new tab (external)
 *   - no url + content present → opens inline modal viewer
 *   - no url + no content      → non-interactive label text
 */
export function LegalFooterLinks({ links, variant = 'caption', spacing = 2 }: Readonly<Props>) {
  
  const { getContent } = useContent();
  const [modalLink, setModalLink] = useState<LegalLink | null>(null);

  const configured = links.filter((l): l is LegalLink => l !== null);
  if (configured.length === 0) return null;

  const resolveLabel = (link: LegalLink): string =>
    getContent(link.title) || link.title;

  const isExternal = (url: string) => /^https?:\/\//i.test(url);
  const fontSize = FONT_SIZE[variant];

  return (
    <>
      <Stack direction="row" spacing={spacing} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {configured.map((link) => {
          const label = resolveLabel(link);

          if (link.url) {
            return (
              <Button
                key={link.documentType}
                component="a"
                href={link.url}
                {...(isExternal(link.url) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                size="small"
                color="inherit"
                sx={{ fontSize, minWidth: 0, '&:hover': { color: 'primary.main' } }}
              >
                {label}
              </Button>
            );
          }

          if (link.content) {
            return (
              <Button
                key={link.documentType}
                size="small"
                color="inherit"
                onClick={() => setModalLink(link)}
                sx={{ fontSize, minWidth: 0, '&:hover': { color: 'primary.main' } }}
              >
                {label}
              </Button>
            );
          }

          return (
            <Typography key={link.documentType} variant={variant} color="text.disabled" component="span" sx={{ fontSize }}>
              {label}
            </Typography>
          );
        })}
      </Stack>

      <LegalDocumentModal link={modalLink} onClose={() => setModalLink(null)} />
    </>
  );
}
